/*
 *   Copyright (c) 2021 Busy Human LLC
 *   All rights reserved.
 *   This file, its contents, concepts, methods, behavior, and operation  (collectively the "Software") are protected by trade secret, patent,  and copyright laws. The use of the Software is governed by a license  agreement. Disclosure of the Software to third parties, in any form,  in whole or in part, is expressly prohibited except as authorized by the license agreement.
 */
// <reference path="@firebase/firestore-types" />
import { deleteDoc, getDoc, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import { cloneDeep as _cloneDeep, merge as _merge } from "lodash-es";
import { default as mitt } from "mitt";
import { SnapshotEventType } from "../common/firebase/snapshot-event.js";
import { dataInstanceToData, DefaultDocumentUpdateOptions, DefaultFetchDataOptions, DefaultGetDataOptions } from "../common/firebase/entity-interfaces.js";
import { resolvePath } from "../common/firebase/firebase-paths.js";
import { flattenData } from "../common/util/index.js";
/**
 * Firestore Alone vs Firestore + DocumentEntity
 * var ref = firestore.doc("users/michael");
 * ref.get()
 * .then(snap => {
 *      var michael = snap.data();
 *      michael.age;
 *
 *      michael.age = 52;
 *      ref.set(michael)
 * });
 *
 * ref.onSnapshot(snapshot => {
 *      michael = snap.data();
 * })
 *
 * entity.fetch()
 *  .then(() => {
 *      entity.age
 *      entity.hairColor = "blue";
 *      entity.update();
 *  });
 */
var RemoteExistsEnum;
(function (RemoteExistsEnum) {
    RemoteExistsEnum[RemoteExistsEnum["exists"] = 0] = "exists";
    RemoteExistsEnum[RemoteExistsEnum["absent"] = 1] = "absent";
    RemoteExistsEnum[RemoteExistsEnum["unknown"] = 2] = "unknown";
})(RemoteExistsEnum || (RemoteExistsEnum = {}));
export class DocumentEntity {
    path = '';
    _data;
    _metadata = null;
    emitter;
    reference = null;
    listenUnsubscribe;
    listenPromise = null;
    listenPromiseResolved = false;
    collection;
    remoteExists;
    constructor(collection, reference, data) {
        this.collection = collection;
        this.emitter = mitt();
        this.listenUnsubscribe = null;
        this.remoteExists = RemoteExistsEnum.unknown;
        if (reference) {
            this.setReference(reference);
        }
        if (data) {
            this._data = { ...data };
        }
        else {
            this._data = this.collection.defaults();
        }
    }
    subPath(...pathParts) {
        return resolvePath(this.path, ...pathParts);
    }
    subCollectionEntity(name) {
        const { relationships } = this.collection;
        if (!relationships)
            throw new Error(`No relationships defined`);
        if (!(name in relationships))
            throw new Error(`No relationship named: "${String(name)}"`);
        const [pathPart, CLS] = relationships[name];
        const path = this.subPath(pathPart);
        return new (CLS)({ path });
    }
    sub(name) {
        return this.subCollectionEntity(name);
    }
    hasReference() {
        return Boolean(this.reference);
    }
    setReference(reference) {
        this.path = reference.path;
        this.reference = reference;
    }
    /**
     * Sets this entity to match another entity
     * @param entity
     */
    matchEntity(entity) {
        if (this.reference) {
            throw new Error(`setEntity can only be called on an unset DocumentEntity`);
        }
        this.collection = entity.collection;
        this.applySnapshot(entity.toSnapshot());
    }
    assertReference() {
        if (!this.reference) {
            throw new Error(`DocumentEntity requires reference for this method`);
        }
        return this.reference;
    }
    get $id() {
        return this.assertReference().id;
    }
    data({ clone } = DefaultGetDataOptions()) {
        const $id = this.reference ? this.$id : null;
        const _data = { $id };
        if (clone) {
            Object.assign(_data, _cloneDeep(this._data));
        }
        else {
            Object.assign(_data, this._data);
        }
        return _data;
    }
    partial(fields, options = DefaultGetDataOptions()) {
        const map = { $id: this.$id };
        const data = this.data(options);
        for (const field of fields) {
            map[field] = data[field];
        }
        return map;
    }
    emitUpdate() {
        this.emitter.emit(SnapshotEventType.Updated, {
            type: SnapshotEventType.Updated,
            target: this
        });
    }
    // private resolveData( data: T | DataInstance<T>) {
    //     var resolvedData: T;
    //     if("id" in data) {
    //         resolvedData = Object.assign({}, dataInstanceToData(data as DataInstance<T>));
    //     } else {
    //         resolvedData = Object.assign({}, data as T);
    //     }
    //     return resolvedData;
    // }
    resolvePartialData(data) {
        const tmp = {};
        // Normalize the argument passed in
        if ("$id" in data) {
            Object.assign(tmp, dataInstanceToData(data));
        }
        else {
            Object.assign(tmp, data);
        }
        return tmp;
    }
    validateData(data) {
        if ("$id" in data) {
            console.trace();
            console.warn("$id should not a field name and will throw an exception in later versions of Gearbox");
        }
    }
    async set(newData) {
        const _ref = this.assertReference();
        if ("$id" in newData) {
            this._data = Object.assign(this.collection.defaults(), dataInstanceToData(newData));
        }
        else {
            this._data = Object.assign(this.collection.defaults(), newData);
        }
        this.validateData(this._data);
        this.emitUpdate();
        const setResult = await setDoc(_ref, this._data);
        this.remoteExists = RemoteExistsEnum.exists;
        return setResult;
    }
    update(newData, options = DefaultDocumentUpdateOptions()) {
        const _ref = this.assertReference();
        const resolvedData = this.resolvePartialData(newData);
        // Merge the data
        if (options.mergeDeep) {
            _merge(this._data, resolvedData);
        }
        else {
            Object.assign(this._data, resolvedData);
        }
        this.validateData(this._data);
        this.emitUpdate();
        return updateDoc(_ref, { ...flattenData(resolvedData) });
    }
    async createOrUpdate(newData, options = DefaultDocumentUpdateOptions()) {
        await this.fetch();
        if (this.remoteExists === RemoteExistsEnum.exists) {
            return this.update(newData, options);
        }
        else {
            const fullData = Object.assign(this.collection.defaults(), dataInstanceToData(newData));
            return this.set(fullData);
        }
    }
    updateWithFunction(field, fn) {
        const value = this._data[field];
        return this.update({
            [field]: fn(value)
        });
    }
    increment(field, amount = 1) {
        if (typeof this._data[field] === "number") {
            let value = this._data[field];
            value += amount;
            return this.update({
                [field]: value
            });
        }
        else {
            console.log(`Value of ${String(field)} `, this._data[field]);
            throw new Error(`${String(field)} is not a number and cannot be incremented.`);
        }
    }
    decrement(field, amount = 1) {
        return this.increment(field, -amount);
    }
    onUpdate(callback) {
        this.emitter.on(SnapshotEventType.Updated, callback);
    }
    listen() {
        const _ref = this.assertReference();
        if (!this.listenUnsubscribe) {
            this.listenPromise = new Promise(res => {
                this.listenUnsubscribe = onSnapshot(_ref, docSnap => {
                    this.applySnapshot(docSnap);
                    if (!this.listenPromiseResolved) {
                        this.listenPromiseResolved = true;
                        res();
                    }
                });
            });
        }
        return this.listenPromise;
    }
    async fetch(options = DefaultFetchDataOptions()) {
        const _ref = this.assertReference();
        const snap = await getDoc(_ref);
        this.applySnapshot(snap);
        return this.data(options);
    }
    applySnapshot(snap) {
        if (!this.reference) {
            this.setReference(snap.ref);
        }
        this.remoteExists = snap.exists() ? RemoteExistsEnum.exists : RemoteExistsEnum.absent;
        Object.assign(this._data, snap.data());
        this.emitUpdate();
    }
    stopListening() {
        if (this.listenUnsubscribe) {
            this.listenUnsubscribe();
            this.listenUnsubscribe = null;
        }
    }
    cleanup() {
        this.emitter.all.clear();
        this.stopListening();
    }
    delete() {
        this.cleanup();
        if (this.reference) {
            return deleteDoc(this.reference);
        }
    }
    toSnapshot() {
        const _ref = this.assertReference();
        const data = this._data;
        const exists = this.remoteExists === RemoteExistsEnum.exists;
        const tmp = {
            // TODO: This isn't actually ever set
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            metadata: this._metadata,
            exists: () => exists,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            data(_options) {
                return data;
            },
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            get(_fieldPath, _options) {
                throw new Error("Function not implemented.");
            },
            id: this.$id,
            ref: _ref
        };
        return tmp;
    }
}
//# sourceMappingURL=document-entity.js.map